<script setup>
import axios from 'axios';
import { BAlert, BForm, BFormCheckbox, BFormGroup } from 'bootstrap-vue-next';
import { reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import LoadingButton from '~/common/components/LoadingButton.vue';

import LabelDropdown from './LabelDropdown.vue';
import { useLabelState } from './useLabelState';

const emits = defineEmits(['error']);

const props = defineProps({
  description: {
    type: String,
    required: true,
  },
  groupId: {
    type: String,
    required: true,
  },
  groups: {
    type: Array,
    required: true,
  },
  iid: {
    type: String,
    required: true,
  },
  isIssue: {
    type: Boolean,
    required: true,
  },
  labels: {
    type: Array,
    required: true,
  },
  labelConfig: {
    type: Object,
    required: true,
  },
  projectId: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  webUrl: {
    type: String,
    required: true,
  },
});

const route = useRoute();
const router = useRouter();

const {
  areaOptions,
  reproducedOptions,
  existing,
  updatable,
  groupOptions,
  availableTypeOptions,
  availableSubtypeOptions,
  categoryOptions,
  typeLocked,
  payload,
} = useLabelState(props);

const loading = ref(false);
const errorMessage = ref('');

const handleSubmit = async () => {
  if (loading.value) {
    return;
  }

  loading.value = true;
  errorMessage.value = '';

  try {
    const url = props.isIssue ? '/api/v1/manage_issue/label' : '/api/v1/manage_merge_request/label';
    await axios.post(
      url,
      {
        issue_iid: props.iid,
        labels: payload.value,
        merge_request_iid: props.iid,
        project_id: props.projectId,
        web_url: props.webUrl,
        issue_title: props.title,
      },
    );
    if (route.query.returnTo) {
      router.push({ name: route.query.returnTo });
    } else {
      window.location.href = props.webUrl;
    }
  } catch (error) {
    loading.value = false;
    if (error.response?.status === 429) {
      errorMessage.value = error.response.data?.error || 'You have exceeded the rate limit for labeling. Please wait a moment before trying again.';
    } else {
      errorMessage.value = 'Failed to update labels. Please try again.';
      emits('error');
    }
  }
};

const aiSuggestions = reactive({
  loading: true,
  error: false,
  areas: null,
});
const fetchAiSuggestions = async () => {
  aiSuggestions.loading = true;
  aiSuggestions.error = false;

  try {
    const url = '/api/v1/manage_issue/suggest_labels';

    const {
      description,
      labels,
      title,
      projectId: project_id,
      groupId: group_id,
    } = props;

    const response = await axios.post(url, {
      description,
      labels,
      title,
      project_id,
      group_id,
    });

    Object.assign(aiSuggestions, response.data);
  } catch {
    aiSuggestions.error = true;
  } finally {
    aiSuggestions.loading = false;
  }
};

fetchAiSuggestions();

const acceptSuggestedAreaLabels = () => {
  if (!aiSuggestions.areas?.deduced?.length) {
    return;
  }

  aiSuggestions.areas.deduced.forEach((area) => {
    updatable.areas[area] = true;
  });
};
</script>

<template>
  <BForm
    @submit.prevent="handleSubmit"
  >
    <p class="text-muted mb-3">
      Not sure which labels to apply? See the
      <a
        href="https://docs.gitlab.com/development/labels"
        target="_blank"
        rel="noopener noreferrer"
      >labels documentation</a>.
    </p>
    <LabelDropdown
      v-model="updatable.type"
      :ai-suggestion="aiSuggestions.type"
      :existing-label="existing.type"
      label="Type"
      :locked="typeLocked"
      :options="availableTypeOptions"
      :ai-error="aiSuggestions.error"
    />
    <LabelDropdown
      v-model="updatable.subtype"
      :ai-suggestion="aiSuggestions.subtype"
      :existing-label="existing.subtype"
      label="Subtype"
      :locked="!!existing.subtype"
      :options="availableSubtypeOptions"
      :ai-error="aiSuggestions.error"
    />
    <LabelDropdown
      v-model="updatable.group"
      :ai-suggestion="aiSuggestions.group"
      :existing-label="existing.group"
      label="Group"
      :locked="!!existing.group"
      :options="groupOptions"
      :ai-error="aiSuggestions.error"
    />
    <LabelDropdown
      v-model="updatable.category"
      :ai-suggestion="aiSuggestions.category"
      :existing-label="existing.category"
      label="Category"
      :locked="!!existing.category || !updatable.group"
      :options="categoryOptions"
      :ai-error="aiSuggestions.error"
    />
    <div>
      <BFormGroup
        label="Areas"
        class="mb-3"
      >
        <BFormCheckbox
          v-for="area in areaOptions"
          :key="area"
          v-model="updatable.areas[area]"
          :disabled="existing.areas.includes(area)"
        >
          {{ area }}
        </BFormCheckbox>
        <template
          #description
        >
          <template v-if="aiSuggestions.loading">
            GitLab Duo AI is fetching suggestions...
          </template>
          <template v-else-if="aiSuggestions.error">
            GitLab Duo AI suggestions are temporarily unavailable
          </template>
          <template v-else-if="aiSuggestions.areas?.deduced?.length === 0">GitLab Duo AI has no suggestions for areas</template>
          <template v-else-if="aiSuggestions.areas?.confidence >= 75">
            GitLab Duo AI confidently suggests
            <a
              href="#"
              @click.prevent="acceptSuggestedAreaLabels"
            >{{ aiSuggestions.areas.deduced.join(', ') }}</a>
          </template>
        </template>
      </BFormGroup>
      <BFormGroup
        v-if="isIssue && (!existing.type || existing.type === 'type::bug')"
        label="Bug reproduction"
        class="mb-3"
        :disabled="updatable.type !== 'type::bug'"
      >
        <BFormCheckbox
          v-for="option in reproducedOptions"
          :key="option"
          v-model="updatable.reproduced[option]"
          :disabled="existing.reproduced.includes(option)"
        >
          {{ option.slice(14) }}
        </BFormCheckbox>
      </BFormGroup>
    </div>
    <BAlert
      v-if="errorMessage"
      :model-value="true"
      variant="danger"
      class="mt-3"
    >
      {{ errorMessage }}
    </BAlert>
    <LoadingButton
      class="mt-2"
      type="submit"
      variant="primary"
      :loading="loading"
      loading-text="Updating..."
    >
      Update labels
    </LoadingButton>
  </BForm>
</template>
